import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { recordPageView } from "@/analytics/pageView";

import { isAppRoute, PUBLIC_ROUTES } from "./routeManifest";
import { absoluteUrl, site } from "./siteConfig";

/**
 * Keeps the document head in step with client-side navigation.
 *
 * Prerendering only fixes the head for the document a visitor *lands* on.
 * Every in-app `<Link>` after that is a client-side route change: React
 * swaps the body and leaves `<title>`, the description and the canonical
 * pointing at whatever page was loaded first. That shows up as a wrong
 * browser-tab title, a wrong canonical for any renderer that follows
 * links without a fresh document request, and wrong OG tags for anything
 * reading the live DOM.
 *
 * Driven by the same manifest that drives prerendering, so a page cannot
 * have one title in its static HTML and another after navigation.
 *
 * The analytics page view rides along here for the same reason the head
 * does: this already runs on every URL change and already knows whether
 * a path is public, app, or 404. One hook, one classification.
 */
const NOT_FOUND_TITLE = "Page Not Found | Revetra";

function setMeta(selector: string, attribute: string, value: string): void {
  const element = document.head.querySelector(selector);
  if (element) element.setAttribute(attribute, value);
}

/**
 * A canonical pointing at the wrong page is worse than none: it tells a
 * renderer that this URL is a duplicate of some unrelated marketing page.
 */
function dropStaleUrlTags(): void {
  document.head.querySelector('link[rel="canonical"]')?.remove();
  document.head.querySelector('meta[property="og:url"]')?.remove();
}

function setRobots(content: string | null): void {
  const existing = document.head.querySelector('meta[name="robots"]');
  if (content === null) {
    existing?.remove();
    return;
  }
  if (existing) {
    existing.setAttribute("content", content);
    return;
  }
  const meta = document.createElement("meta");
  meta.setAttribute("name", "robots");
  meta.setAttribute("content", content);
  document.head.appendChild(meta);
}

export function useDocumentHead(): void {
  const { pathname } = useLocation();
  /**
   * The last path recorded, so a page view fires once per actual change.
   *
   * `null` means "nothing recorded yet", i.e. the document the visitor
   * landed on — which the container's own configuration tag has already
   * counted. Recording that would double every entry page.
   *
   * Keyed on the path rather than a first-mount flag because StrictMode
   * invokes this effect twice on mount in development; a boolean would
   * let the second pass record the landing page after all.
   */
  const recordedPath = useRef<string | null>(null);

  useEffect(() => {
    // Trailing slashes are equivalent for our routes but would miss here.
    const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
    const route = PUBLIC_ROUTES.find((candidate) => candidate.path === normalized);

    if (route) {
      const canonical = absoluteUrl(route.path);
      document.title = route.title;
      setRobots(null);
      setMeta('meta[name="description"]', "content", route.description);
      setMeta('link[rel="canonical"]', "href", canonical);
      setMeta('meta[property="og:title"]', "content", route.title);
      setMeta('meta[property="og:description"]', "content", route.description);
      setMeta('meta[property="og:url"]', "content", canonical);
      setMeta('meta[property="og:site_name"]', "content", site.site_name);
      setMeta('meta[name="twitter:title"]', "content", route.title);
      setMeta('meta[name="twitter:description"]', "content", route.description);
      if (recordedPath.current !== null && recordedPath.current !== normalized) {
        recordPageView(route.path, route.title);
      }
      recordedPath.current = normalized;
      return;
    }

    if (isAppRoute(normalized)) {
      // No noindex here on purpose: these URLs are robots.txt-disallowed,
      // and each route gets exactly one crawl-control mechanism. Just stop
      // claiming they are some marketing page.
      document.title = site.site_name;
      dropStaleUrlTags();
      setRobots(null);
      // No page view, ever. Reaching here by client-side navigation is
      // already meant to be impossible — AppEntryLink makes public → app
      // a full document load — so this is the backstop, not the control.
      recordedPath.current = normalized;
      return;
    }

    // Anything else is the client-rendered 404. Give it the same head the
    // static 404.html gets, since unknown paths are not robots-blocked.
    document.title = NOT_FOUND_TITLE;
    dropStaleUrlTags();
    setRobots("noindex, follow");
    if (recordedPath.current !== null && recordedPath.current !== normalized) {
      recordPageView(normalized, NOT_FOUND_TITLE);
    }
    recordedPath.current = normalized;
  }, [pathname]);
}
